import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TextField, Switch } from '@mui/material';
import { Container, Row, Col, Card, CardBody, CardHeader, Button, ButtonGroup, Table, Nav, NavItem, NavLink, TabContent, TabPane, Form, FormGroup, Label, Input, Badge, Alert, Modal, ModalHeader, ModalBody, ModalFooter, InputGroup, InputGroupText, CustomInput, Progress, Pagination, PaginationItem, PaginationLink, UncontrolledTooltip } from 'reactstrap';
import { 
  getAuditLogs, 
  getSecuritySettings, 
  updateSecuritySettings, 
  createApiKey, 
  revokeApiKey, 
  addWhitelistIp, 
  removeWhitelistIp,
  getComplianceStatus,
  updateGDPRCompliance,
  updateDataRetention,
  getDataRequests,
  createDataRequest,
  processDataRequest,
  generateComplianceReport
} from '../../../store/Admin';
import {
  ACTION_CATEGORIES,
  getActionCategory,
  getActionImpact,
  formatActionName,
  getActionIcon,
  getStatusBadge,
  formatRelativeTime,
  formatTarget,
  formatChangeDetails,
  QUICK_FILTERS,
  calculateActivityStats,
  exportAuditLogs
} from '../../../utils/auditLogHelpers';
import '../Admin.css';

const SecurityCompliance = () => {
  const dispatch = useDispatch();
  const { auditLogs = [], auditLogsPagination = {}, securitySettings = {}, apiKeys = [], ipWhitelist = [], complianceStatus = {}, loading } = useSelector(state => state['Admin']);

  const [activeTab, setActiveTab] = useState('audit');
  const [currentPage, setCurrentPage] = useState(1);
  const [filterType, setFilterType] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [newApiKey, setNewApiKey] = useState({ name: '', permissions: [] });
  const [newIp, setNewIp] = useState('');
  const [tempSettings, setTempSettings] = useState({});
  const [activeQuickFilter, setActiveQuickFilter] = useState('all');
  const [dataRequestForm, setDataRequestForm] = useState({ type: '', requestedBy: '', notes: '' });
  const [purgeDataType, setPurgeDataType] = useState('');
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [activityStats, setActivityStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    dispatch(getAuditLogs({ type: filterType, user: filterUser, dateRange }));
    if (activeTab === 'settings') dispatch(getSecuritySettings());
    if (activeTab === 'compliance') {
      dispatch(getComplianceStatus());
      dispatch(getDataRequests());
    }
  }, [filterType, filterUser, dateRange, activeTab]);

  useEffect(() => {
    if (auditLogs && auditLogs.length > 0) {
      const stats = calculateActivityStats(auditLogs);
      setActivityStats(stats);
    }
  }, [auditLogs]);

  useEffect(() => {
    setTempSettings(securitySettings);
  }, [securitySettings]);

  const handleSettingChange = (category, setting, value) => {
    setTempSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [setting]: value
      }
    }));
  };

  const saveSecuritySettings = () => {
    dispatch(updateSecuritySettings(tempSettings));
  };

  const openModal = (type, data = null) => {
    setModalType(type);
    if (type === 'logDetails' && data) setSelectedLog(data);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType('');
    setSelectedLog(null);
    setNewApiKey({ name: '', permissions: [] });
    setNewIp('');
  };

  const generateApiKey = () => {
    dispatch(createApiKey({ name: newApiKey['name'], permissions: newApiKey['permissions'] }));
    closeModal();
  };

  const revokeApiKeyLocal = (key) => {
    dispatch(revokeApiKey({ key }));
  };

  const addIpToWhitelist = () => {
    if (!newIp || ipWhitelist.includes(newIp)) return;
    dispatch(addWhitelistIp({ ip: newIp }));
    closeModal();
  };

  const removeIpFromWhitelistLocal = (ip) => {
    dispatch(removeWhitelistIp({ ip }));
  };

  const handleQuickFilter = (filterId) => {
    setActiveQuickFilter(filterId);
    const filter = QUICK_FILTERS.find(f => f.id === filterId);

    if (filter.filter.dateRange === 'today') {
      const today = new Date();
      setDateRange({
        start: today.toISOString().split('T')[0],
        end: today.toISOString().split('T')[0]
      });
    } else if (filter.filter.success === false) {
      // This would need backend support for success filter
      setFilterType('failed');
    } else if (filter.filter.category) {
      // Filter by category
      const category = ACTION_CATEGORIES[filter.filter.category];
      if (category) {
        setFilterType(category.actions[0]); // Use first action as example
      }
    } else {
      // Clear filters for 'all'
      setFilterType('');
      setFilterUser('');
      setDateRange({ start: '', end: '' });
    }

    setCurrentPage(1);
  };

  const handleExportLogs = () => {
    exportAuditLogs(auditLogs, 'csv');
  };

  // Filter logs based on search and filters
  const filteredLogs = auditLogs.filter(log => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const adminName = `${log.adminId?.firstName} ${log.adminId?.lastName}`.toLowerCase();
      const action = formatActionName(log.action).toLowerCase();
      const resource = formatTarget(log.resource, log.resourceId, log.metadata).toLowerCase();

      if (!adminName.includes(searchLower) &&
        !action.includes(searchLower) &&
        !resource.includes(searchLower)) {
        return false;
      }
    }
    return true;
  });

  const renderActionBadge = (action) => {
    const category = getActionCategory(action);
    const icon = getActionIcon(action);
    const name = formatActionName(action);

    return (
      <div className="d-flex align-items-center">
        <i className={`fas ${icon} mr-2`} style={{ color: category.color }}></i>
        <div>
          <div className="font-weight-medium">{name}</div>
          <Badge color="light" className="mt-1">
            <i className={`fas ${category.icon} mr-1`} />
            {category.name}
          </Badge>
        </div>
      </div>
    );
  };

  const renderStatusBadge = (success) => {
    const status = getStatusBadge(success);
    return (
      <Badge color={status.color} pill>
        <i className={`fas ${status.icon} mr-1`}></i>
        {status.text}
      </Badge>
    );
  };

  const renderImpactBadge = (action) => {
    const impact = getActionImpact(action);
    return (
      <Badge color={impact.color} className="px-2">
        <i className={`fas ${impact.icon} mr-1`}></i>
        {impact.level}
      </Badge>
    );
  };

  if (loading) return <Container fluid className="mt-4"><div className="text-center">Loading security settings...</div></Container>;

  return (
    <>
      <div className='content-dashboard'>
        <h3>Security & Compliance</h3>
        <p className="text-muted mb-3">Manage security settings, audit logs, and compliance requirements</p>

        <div className='tabs-area'>
          {/* Navigation Tabs */}
          <div className='tab-header'>
            {/* Navigation Tabs */}
            <Nav tabs className="mb-4">
              <NavItem>
                <NavLink className={activeTab === 'audit' ? 'active' : ''} onClick={() => setActiveTab('audit')}>Audit Logs</NavLink>
              </NavItem>
              <NavItem>
                <NavLink className={activeTab === 'settings' ? 'active' : ''} onClick={() => setActiveTab('settings')}>Security Settings</NavLink>
              </NavItem>
              <NavItem>
                <NavLink className={activeTab === 'api' ? 'active' : ''} onClick={() => setActiveTab('api')}>API Keys</NavLink>
              </NavItem>
              <NavItem>
                <NavLink className={activeTab === 'access' ? 'active' : ''} onClick={() => setActiveTab('access')}>Access Control</NavLink>
              </NavItem>
              <NavItem>
                <NavLink className={activeTab === 'compliance' ? 'active' : ''} onClick={() => setActiveTab('compliance')}>Compliance</NavLink>
              </NavItem>
            </Nav>
          </div>
        </div>

        <TabContent activeTab={activeTab}>
          {/* Audit Logs Tab */}
          <TabPane tabId="audit">
            {/* Activity Insights Dashboard */}
            {activityStats && (
              <div className='counter-sections mb-3'>
                <div className='counter-box style-two'>
                  <div className='counter-start' >
                    <div className='left-text-area'>
                      <h5>Today's Activity</h5>
                      <div className='CountUp-box'>
                        <h3 className="" style={{ color: '#9333ea' }}>{activityStats.today}</h3>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='counter-box style-two'>
                  <div className='counter-start' >
                    <div className='left-text-area'>
                      <h5>Failed Actions</h5>
                      <div className='CountUp-box'>
                        <h3 className="text-success">{activityStats.failed}</h3>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='counter-box style-two'>
                  <div className='counter-start' >
                    <div className='left-text-area'>
                      <h5>Critical Events</h5>
                      <div className='CountUp-box'>
                        <h3 className="" style={{ color: '#EF4D56' }}>{activityStats.critical}</h3>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='counter-box style-two'>
                  <div className='counter-start' >
                    <div className='left-text-area'>
                      <h5>Total Actions</h5>
                      <div className='CountUp-box'>
                        <h3 className="" style={{ color: '#9333EA' }}>{activityStats.total}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Filters */}
            <div className="mb-3">
              <ButtonGroup className="security-tab">
                {QUICK_FILTERS.map(filter => (
                  <Button
                    key={filter.id}
                    color={activeQuickFilter === filter.id ? filter.color || 'primary' : 'light'}
                    size="sm"
                    onClick={() => handleQuickFilter(filter.id)}
                  >
                    <i className={`fas ${filter.icon} mr-1`}></i>
                    {filter.label}
                  </Button>
                ))}
              </ButtonGroup>
            </div>

            <div className='title-area audit-title'>
              <h4>Audit Trail</h4>
              <div>
                <button className='btn-style-border mr-2' onClick={handleExportLogs}>
                  <i className="fa fa-download"></i> Export
                </button>
                <button className='btn-style-border' onClick={() => dispatch(getAuditLogs({ type: filterType, user: filterUser, dateRange }))}>
                  <i className="fa fa-sync"></i> Refresh
                </button>
              </div>
            </div>
            <div className="table-container filters-table" style={{ overflowX: 'auto' }}>
              {/* Search and Filters */}
              <div className='header-table-filters advanced-table-filters secuirty-tab'>
                <div className='left-area'>
                  <div className='filter-box'>
                    <InputGroup>
                      <InputGroupText>
                        <i className="fas fa-search"></i>
                      </InputGroupText>
                      <Input
                        placeholder="Search logs..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </div>
                  <div className='filter-box'>
                    <Input type="select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                      <option value="">All Actions</option>
                      <option value="create">Create</option>
                      <option value="update">Update</option>
                      <option value="delete">Delete</option>
                      <option value="login">Login</option>
                      <option value="logout">Logout</option>
                      <option value="view">View</option>
                      <option value="export">Export</option>
                    </Input>
                  </div>
                  <div className='filter-box'>
                    <Input placeholder="Filter by user..." value={filterUser} onChange={(e) => setFilterUser(e.target.value)} />
                  </div>
                  <div className='filter-box'>
                    <Input type="date" value={dateRange['start']} onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })} />
                  </div>
                  <div className='filter-box'>
                    <Input type="date" value={dateRange['end']} onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })} />
                  </div>
                </div>
              </div>
              <div className='table-area'>
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Admin</th>
                      <th>Action</th>
                      <th>Target</th>
                      <th>Status</th>
                      <th>Impact</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.length > 0 ? (
                      filteredLogs.map((log) => {
                          const category = getActionCategory(log.action);
                          const impact = getActionImpact(log.action);
                          const status = getStatusBadge(log.success);

                          return (
                            <tr key={log._id}>
                              <td>
                                <small>
                                  {log.createdAt ? 
                                    new Date(log.createdAt).toLocaleDateString('en-US', {
                                      month: '2-digit',
                                      day: '2-digit',
                                      year: 'numeric'
                                    }) : 
                                    new Date().toLocaleDateString('en-US', {
                                      month: '2-digit',
                                      day: '2-digit',
                                      year: 'numeric'
                                    })}
                                </small>
                              </td>
                              <td>
                                <div>
                                  <strong>
                                    {log.adminId ? 
                                      `${log.adminId.firstName || 'Unknown'} ${log.adminId.lastName || ''}` : 
                                      'System'}
                                  </strong>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <i className={`fas ${getActionIcon(log.action)} mr-2`} 
                                     style={{ color: category.color, fontSize: '0.875rem' }} />
                                  <span>{formatActionName(log.action)}</span>
                                </div>
                              </td>
                              <td>
                                <div className="text-truncate" style={{ maxWidth: '200px' }}>
                                  {formatTarget(log.resource, log.resourceId, log.metadata)}
                                </div>
                              </td>
                              <td>
                                <Badge color={status.color} pill>
                                  {status.text}
                                </Badge>
                              </td>
                              <td>
                                <Badge color={impact.color}>
                                  {impact.level}
                                </Badge>
                              </td>
                              <td>
                                <button
                                  color="link"
                                  size="sm"
                                  onClick={() => openModal('logDetails', log)}
                                  title="View Details"
                                  className="edit-table-btn"
                                >
                                  <i class="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.1" cx="12" cy="12" r="12" fill="#323B43"></circle><path d="M12 14.165C10.805 14.165 9.83496 13.195 9.83496 12C9.83496 10.805 10.805 9.83501 12 9.83501C13.195 9.83501 14.165 10.805 14.165 12C14.165 13.195 13.195 14.165 12 14.165ZM12 10.585C11.22 10.585 10.585 11.22 10.585 12C10.585 12.78 11.22 13.415 12 13.415C12.78 13.415 13.415 12.78 13.415 12C13.415 11.22 12.78 10.585 12 10.585Z" fill="#323B43"></path><path d="M12 16.51C10.12 16.51 8.34504 15.41 7.12504 13.5C6.59504 12.675 6.59504 11.33 7.12504 10.5C8.35004 8.58999 10.125 7.48999 12 7.48999C13.875 7.48999 15.65 8.58999 16.87 10.5C17.4 11.325 17.4 12.67 16.87 13.5C15.65 15.41 13.875 16.51 12 16.51ZM12 8.23999C10.385 8.23999 8.84004 9.20999 7.76004 10.905C7.38504 11.49 7.38504 12.51 7.76004 13.095C8.84004 14.79 10.385 15.76 12 15.76C13.615 15.76 15.16 14.79 16.24 13.095C16.615 12.51 16.615 11.49 16.24 10.905C15.16 9.20999 13.615 8.23999 12 8.23999Z" fill="#323B43"></path></svg></i>
                                </button>
                              </td>
                            </tr>
                          );
                        })
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-4">
                          <div className="text-muted">
                            <i className="fas fa-inbox fa-2x mb-2" />
                            <p className="mb-0">No audit logs found</p>
                            <small>Try adjusting your filters or date range</small>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                {/* Total entries info */}
                <div className="d-flex justify-content-end align-items-center mt-3">
                  <div className="text-muted">
                    Total: {filteredLogs.length} {filteredLogs.length === 1 ? 'entry' : 'entries'}
                  </div>
                </div>
              </div>
            </div>
          </TabPane>

          {/* Security Settings Tab */}
          <TabPane tabId="settings">
            <div className='chart-box'>
              <Form className="dashboard-form security-form">
                <div className='row'>
                  <div className='col-lg-6 col-md-12 col-sm-12'>
                    <div className='chart-area'>
                      <div className='table-area table-workspaces'>
                        <h4>Call Statistics</h4>
                        <FormGroup>
                          <Label>Two-Factor Authentication</Label>
                          <Switch id="2fa" checked={tempSettings['authentication']?.['twoFactor'] || false} onChange={(e) => handleSettingChange('authentication', 'twoFactor', e.target.checked)} />
                        </FormGroup>
                        <FormGroup>
                          <Label>Session Timeout (minutes)</Label>
                          <Input type="number" style={{ width: '100px' }} value={tempSettings['authentication']?.['sessionTimeout'] || 30} onChange={(e) => handleSettingChange('authentication', 'sessionTimeout', parseInt(e.target.value))} />
                        </FormGroup>
                        <FormGroup>
                          <Label>Password Complexity</Label>
                          <Switch id="pwdComplexity" checked={tempSettings['authentication']?.['passwordComplexity'] || false} onChange={(e) => handleSettingChange('authentication', 'passwordComplexity', e.target.checked)} />
                        </FormGroup>
                        <FormGroup>
                          <Label>Max Login Attempts</Label>
                          <Input type="number" style={{ width: '100px' }} value={tempSettings['authentication']?.['maxLoginAttempts'] || 5} onChange={(e) => handleSettingChange('authentication', 'maxLoginAttempts', parseInt(e.target.value))} />
                        </FormGroup>
                      </div>
                    </div>
                  </div>

                  <div className='col-lg-6 col-md-12 col-sm-12'>
                    <div className='chart-area'>
                      <div className='table-area table-workspaces'>
                        <h4>Security Monitoring</h4>
                        <FormGroup>
                          <Label>Anomaly Detection</Label>
                          <Switch id="anomalyDetection" checked={tempSettings['monitoring']?.['anomalyDetection'] || false} onChange={(e) => handleSettingChange('monitoring', 'anomalyDetection', e.target.checked)} />
                        </FormGroup>
                        <FormGroup>
                          <Label>Real-time Alerts</Label>
                          <Switch id="realtimeAlerts" checked={tempSettings['monitoring']?.['realtimeAlerts'] || false} onChange={(e) => handleSettingChange('monitoring', 'realtimeAlerts', e.target.checked)} />
                        </FormGroup>
                        <FormGroup>
                          <Label>Failed Login Alerts</Label>
                          <Switch id="failedLoginAlerts" checked={tempSettings['monitoring']?.['failedLoginAlerts'] || false} onChange={(e) => handleSettingChange('monitoring', 'failedLoginAlerts', e.target.checked)} />
                        </FormGroup>
                        <FormGroup>
                          <Label>Alert Email</Label>
                          <Input type="email" style={{ width: '70%' }} value={tempSettings['monitoring']?.['alertEmail'] || ''} onChange={(e) => handleSettingChange('monitoring', 'alertEmail', e.target.value)} />
                        </FormGroup>
                      </div>
                    </div>
                  </div>

                  <div className='col-lg-6 col-md-12 col-sm-12'>
                    <div className='chart-area'>
                      <div className='table-area table-workspaces'>
                        <h4>Data Protection</h4>
                        <FormGroup>
                          <label>Encryption at Rest</label>
                          <Switch id="encryptionRest" checked={tempSettings['dataProtection']?.['encryptionAtRest'] || false} onChange={(e) => handleSettingChange('dataProtection', 'encryptionAtRest', e.target.checked)} />
                        </FormGroup>
                        <FormGroup>
                          <label>Data Retention (days)</label>
                          <Input type="number" style={{ width: '100px' }} value={tempSettings['dataProtection']?.['dataRetention'] || 90} onChange={(e) => handleSettingChange('dataProtection', 'dataRetention', parseInt(e.target.value))} />
                        </FormGroup>
                        <FormGroup>
                          <label>Automatic Backups</label>
                          <Switch id="autoBackup" checked={tempSettings['dataProtection']?.['automaticBackups'] || false} onChange={(e) => handleSettingChange('dataProtection', 'automaticBackups', e.target.checked)} />
                        </FormGroup>
                      </div>
                    </div>
                  </div>

                  <div className='col-lg-6 col-md-12 col-sm-12'>
                    <div className='chart-area'>
                      <div className='table-area table-workspaces'>
                        <h4>API Security</h4>
                        <FormGroup>
                          <label>Rate Limiting</label>
                          <Switch id="rateLimiting" checked={tempSettings['apiSecurity']?.['rateLimiting'] || false} onChange={(e) => handleSettingChange('apiSecurity', 'rateLimiting', e.target.checked)} />
                        </FormGroup>
                        <FormGroup>
                          <label>Requests per Minute</label>
                          <Input type="number" style={{ width: '100px' }} value={tempSettings['apiSecurity']?.['requestsPerMinute'] || 60} onChange={(e) => handleSettingChange('apiSecurity', 'requestsPerMinute', parseInt(e.target.value))} />
                        </FormGroup>
                        <FormGroup>
                          <label>API Key Required</label>
                          <Switch id="apiKeyRequired" checked={tempSettings['apiSecurity']?.['apiKeyRequired'] || false} onChange={(e) => handleSettingChange('apiSecurity', 'apiKeyRequired', e.target.checked)} />
                        </FormGroup>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='btn-save-area text-right mt-4'>
                  <button className='btn-style-new' onClick={saveSecuritySettings}>
                    <i className="icon fa fa-save"></i>Save Security Settings
                  </button>
                </div>
              </Form>
            </div>
          </TabPane>

          {/* API Keys Tab */}
          <TabPane tabId="api">
            <div className='title-area audit-title'>
              <h4>API Keys Management</h4>
              <button className='btn-style-border' onClick={() => openModal('createApiKey')}>
                + Generate New API Key
              </button>
            </div>
            <div className="table-container filters-table">
              <div className='table-area'>
                <Table responsive>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Key</th>
                      <th>Permissions</th>
                      <th>Created</th>
                      <th>Last Used</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiKeys.map((apiKey, idx) => (
                      <tr key={idx}>
                        <td>{apiKey['name']}</td>
                        <td>
                          <code color='warning-color'>{apiKey['key'].substring(0, 10)}...{apiKey['key'].substring(apiKey['key'].length - 6)}</code>
                          <Button size="sm" color="link" className="ml-2" >
                            <i className="fa fa-copy"></i>
                          </Button>
                        </td>
                        <td>
                          {apiKey['permissions']?.map(perm => (
                            <Badge key={perm} className={`mr-1 perm-${perm.toLowerCase().replace(/\s+/g, '-')}`}>{perm}</Badge>
                          ))}
                        </td>
                        <td>{new Date(apiKey['createdAt']).toLocaleDateString()}</td>
                        <td>{apiKey['lastUsed'] ? new Date(apiKey['lastUsed']).toLocaleDateString() : 'Never'}</td>
                        <td>
                          <div className='action-box'>
                            <button className='action-btn' color="danger-color" onClick={() => revokeApiKeyLocal(apiKey['key'])}>
                              <i className="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle opacity="0.1" cx="12" cy="12" r="12" fill="#EF4D56" />
                                <path d="M17.2501 8.92584C17.2385 8.92584 17.221 8.92584 17.2035 8.92584C14.1176 8.61667 11.0376 8.50001 7.9868 8.80917L6.7968 8.92584C6.5518 8.94917 6.33597 8.77417 6.31263 8.52917C6.2893 8.28417 6.4643 8.07417 6.70347 8.05084L7.89347 7.93417C10.9968 7.61917 14.141 7.74167 17.291 8.05084C17.5301 8.07417 17.7051 8.29001 17.6818 8.52917C17.6643 8.75667 17.4718 8.92584 17.2501 8.92584Z" fill="#EF4D56" />
                                <path d="M9.95844 8.33667C9.93511 8.33667 9.91177 8.33667 9.88261 8.33084C9.64927 8.29001 9.48594 8.06251 9.52677 7.82917L9.65511 7.06501C9.74844 6.50501 9.87677 5.72917 11.2359 5.72917H12.7643C14.1293 5.72917 14.2576 6.53417 14.3451 7.07084L14.4734 7.82917C14.5143 8.06834 14.3509 8.29584 14.1176 8.33084C13.8784 8.37167 13.6509 8.20834 13.6159 7.97501L13.4876 7.21667C13.4059 6.70917 13.3884 6.61001 12.7701 6.61001H11.2418C10.6234 6.61001 10.6118 6.69167 10.5243 7.21084L10.3901 7.96917C10.3551 8.18501 10.1684 8.33667 9.95844 8.33667Z" fill="#EF4D56" />
                                <path d="M13.8724 18.2708H10.1274C8.09157 18.2708 8.0099 17.145 7.94573 16.235L7.56657 10.3608C7.54907 10.1217 7.73573 9.91167 7.9749 9.89417C8.2199 9.8825 8.42407 10.0633 8.44157 10.3025L8.82073 16.1767C8.8849 17.0633 8.90823 17.3958 10.1274 17.3958H13.8724C15.0974 17.3958 15.1207 17.0633 15.1791 16.1767L15.5582 10.3025C15.5757 10.0633 15.7857 9.8825 16.0249 9.89417C16.2641 9.91167 16.4507 10.1158 16.4332 10.3608L16.0541 16.235C15.9899 17.145 15.9082 18.2708 13.8724 18.2708Z" fill="#EF4D56" />
                                <path d="M12.9684 15.0625H11.0259C10.7867 15.0625 10.5884 14.8642 10.5884 14.625C10.5884 14.3858 10.7867 14.1875 11.0259 14.1875H12.9684C13.2075 14.1875 13.4059 14.3858 13.4059 14.625C13.4059 14.8642 13.2075 15.0625 12.9684 15.0625Z" fill="#EF4D56" />
                                <path d="M13.4582 12.7292H10.5415C10.3023 12.7292 10.104 12.5308 10.104 12.2917C10.104 12.0525 10.3023 11.8542 10.5415 11.8542H13.4582C13.6973 11.8542 13.8957 12.0525 13.8957 12.2917C13.8957 12.5308 13.6973 12.7292 13.4582 12.7292Z" fill="#EF4D56" />
                              </svg></i>
                              Revoke
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          </TabPane>

          {/* Access Control Tab */}
          <TabPane tabId="access">
            <div className='chart-box'>
              <div className='row'>
                <div className='col-lg-6 col-md-12 col-sm-12'>
                  <div className='chart-area'>
                    <div className='table-area table-workspaces'>
                      <div className='table-title-area d-flex justify-content-between align-items-center mb-2'>
                        <h4>IP Whitelist</h4>
                        <button className='btn-style-border' onClick={() => openModal('addIp')}>+  Add IP</button>
                      </div>
                      {ipWhitelist.length === 0 ? (
                        <Alert color="info">No IP addresses whitelisted. All IPs are allowed.</Alert>
                      ) : (
                        <Table>
                          <thead>
                            <tr>
                              <th>IP Address</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ipWhitelist.map((ip, idx) => (
                              <tr key={idx}>
                                <td>{ip}</td>
                                <td>
                                  <div className='action-box'>
                                    <button className='action-btn' color="danger-color" onClick={() => removeIpFromWhitelistLocal(ip)}>
                                      <i className="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle opacity="0.1" cx="12" cy="12" r="12" fill="#EF4D56" />
                                        <path d="M17.2501 8.92584C17.2385 8.92584 17.221 8.92584 17.2035 8.92584C14.1176 8.61667 11.0376 8.50001 7.9868 8.80917L6.7968 8.92584C6.5518 8.94917 6.33597 8.77417 6.31263 8.52917C6.2893 8.28417 6.4643 8.07417 6.70347 8.05084L7.89347 7.93417C10.9968 7.61917 14.141 7.74167 17.291 8.05084C17.5301 8.07417 17.7051 8.29001 17.6818 8.52917C17.6643 8.75667 17.4718 8.92584 17.2501 8.92584Z" fill="#EF4D56" />
                                        <path d="M9.95844 8.33667C9.93511 8.33667 9.91177 8.33667 9.88261 8.33084C9.64927 8.29001 9.48594 8.06251 9.52677 7.82917L9.65511 7.06501C9.74844 6.50501 9.87677 5.72917 11.2359 5.72917H12.7643C14.1293 5.72917 14.2576 6.53417 14.3451 7.07084L14.4734 7.82917C14.5143 8.06834 14.3509 8.29584 14.1176 8.33084C13.8784 8.37167 13.6509 8.20834 13.6159 7.97501L13.4876 7.21667C13.4059 6.70917 13.3884 6.61001 12.7701 6.61001H11.2418C10.6234 6.61001 10.6118 6.69167 10.5243 7.21084L10.3901 7.96917C10.3551 8.18501 10.1684 8.33667 9.95844 8.33667Z" fill="#EF4D56" />
                                        <path d="M13.8724 18.2708H10.1274C8.09157 18.2708 8.0099 17.145 7.94573 16.235L7.56657 10.3608C7.54907 10.1217 7.73573 9.91167 7.9749 9.89417C8.2199 9.8825 8.42407 10.0633 8.44157 10.3025L8.82073 16.1767C8.8849 17.0633 8.90823 17.3958 10.1274 17.3958H13.8724C15.0974 17.3958 15.1207 17.0633 15.1791 16.1767L15.5582 10.3025C15.5757 10.0633 15.7857 9.8825 16.0249 9.89417C16.2641 9.91167 16.4507 10.1158 16.4332 10.3608L16.0541 16.235C15.9899 17.145 15.9082 18.2708 13.8724 18.2708Z" fill="#EF4D56" />
                                        <path d="M12.9684 15.0625H11.0259C10.7867 15.0625 10.5884 14.8642 10.5884 14.625C10.5884 14.3858 10.7867 14.1875 11.0259 14.1875H12.9684C13.2075 14.1875 13.4059 14.3858 13.4059 14.625C13.4059 14.8642 13.2075 15.0625 12.9684 15.0625Z" fill="#EF4D56" />
                                        <path d="M13.4582 12.7292H10.5415C10.3023 12.7292 10.104 12.5308 10.104 12.2917C10.104 12.0525 10.3023 11.8542 10.5415 11.8542H13.4582C13.6973 11.8542 13.8957 12.0525 13.8957 12.2917C13.8957 12.5308 13.6973 12.7292 13.4582 12.7292Z" fill="#EF4D56" />
                                      </svg></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </div>
                  </div>
                </div>

                <div className='col-lg-6 col-md-12 col-sm-12'>
                  <div className='chart-area'>
                    <div className='table-area table-workspaces'>
                      <div className='table-title-area d-flex justify-content-between align-items-center mb-2'>
                        <h4>Role-Based Access Control</h4>
                      </div>
                      <Table>
                        <thead>
                          <tr>
                            <th>Role</th>
                            <th>Users</th>
                            <th>Permissions</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td><Badge color="danger">Admin</Badge></td>
                            <td>{securitySettings['roles']?.['admin']?.['count'] || 0}</td>
                            <td>Full Access</td>
                          </tr>
                          <tr>
                            <td><Badge color="primary">Owner</Badge></td>
                            <td>{securitySettings['roles']?.['owner']?.['count'] || 0}</td>
                            <td>Workspace Management</td>
                          </tr>
                          <tr>
                            <td><Badge color="info">Member</Badge></td>
                            <td>{securitySettings['roles']?.['member']?.['count'] || 0}</td>
                            <td>Limited Access</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabPane>


          {/* Compliance Tab */}
          <TabPane tabId="compliance">
            <div className='chart-box'>
              <div className='row'>
                <div className='col-lg-6 col-md-12 col-sm-12'>
                  <div className='chart-area'>
                    <div className='table-area table-workspaces'>
                      <h4>GDPR Compliance</h4>
                      <div className="progress-area-box">
                        <div className="d-flex justify-content-between align-items-center mb-2 mt-3">
                          <h5>Data Subject Rights</h5>
                          <Badge color={complianceStatus?.gdpr?.dataSubjectRights?.accessEnabled ? "success" : "warning"}>
                            {complianceStatus?.gdpr?.dataSubjectRights?.accessEnabled ? "Compliant" : "In Progress"}
                          </Badge>
                        </div>
                        <Progress 
                          value={complianceStatus?.gdpr?.complianceScore || 0} 
                          color={complianceStatus?.gdpr?.complianceScore >= 80 ? "success" : "warning"} 
                        />
                      </div>
                      <div className="progress-area-box">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h5>Privacy Policy</h5>
                          <Badge color={complianceStatus?.gdpr?.privacyPolicy?.lastUpdated ? "success" : "danger"}>
                            {complianceStatus?.gdpr?.privacyPolicy?.lastUpdated ? "Updated" : "Needs Update"}
                          </Badge>
                        </div>
                        <small className="text-muted">
                          Last updated: {complianceStatus?.gdpr?.privacyPolicy?.lastUpdated ? 
                            new Date(complianceStatus.gdpr.privacyPolicy.lastUpdated).toLocaleDateString() : 
                            'Never'}
                        </small>
                      </div>
                      <div className="progress-area-box">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h5>Data Processing Agreements</h5>
                          <Badge color={complianceStatus?.gdpr?.dpa?.inPlace ? "success" : "warning"}>
                            {complianceStatus?.gdpr?.dpa?.inPlace ? "In Place" : "Pending"}
                          </Badge>
                        </div>
                        <button 
                          className='btn-style-border w100'
                          onClick={() => dispatch(generateComplianceReport({ type: 'gdpr' }))}
                        >
                          <i className="icon fa fa-download"></i>Download GDPR Report
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='col-lg-6 col-md-12 col-sm-12'>
                  <div className='chart-area'>
                    <div className='table-area table-workspaces'>
                      <div className='table-title-area d-flex justify-content-between align-items-center mb-2'>
                        <h4>Data Retention Policy</h4>
                      </div>
                      <Table>
                        <thead>
                          <tr>
                            <th>Data Type</th>
                            <th>Retention Period</th>
                            <th>Auto-Delete</th>
                          </tr>
                        </thead>
                        <tbody>
                          {complianceStatus?.dataRetention && complianceStatus.dataRetention.length > 0 ? (
                            complianceStatus.dataRetention.map((policy, idx) => (
                              <tr key={idx}>
                                <td>{policy.dataType}</td>
                                <td>{policy.retentionDays} days</td>
                                <td>
                                  <Badge color={policy.autoDelete ? "success" : "warning"}>
                                    {policy.autoDelete ? "Enabled" : "Manual"}
                                  </Badge>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="3" className="text-center text-muted">
                                No retention policies configured
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </div>

                <div className='col-lg-6 col-md-12 col-sm-12'>
                  <div className='chart-area'>
                    <div className='table-area table-workspaces'>
                      <h4>SOC 2 Compliance</h4>
                      <div className="progress-area-box">
                        <div className="d-flex justify-content-between align-items-center mb-2 mt-3">
                          <h5>Security Controls</h5>
                          <Badge color={
                            complianceStatus?.soc2?.security?.status === 'compliant' ? 'success' : 
                            complianceStatus?.soc2?.security?.status === 'in-progress' ? 'warning' : 'danger'
                          }>
                            {complianceStatus?.soc2?.security?.status || 'In Progress'}
                          </Badge>
                        </div>
                        <Progress 
                          value={complianceStatus?.soc2?.security?.score || 75} 
                          color={
                            complianceStatus?.soc2?.security?.score >= 90 ? 'success' : 
                            complianceStatus?.soc2?.security?.score >= 70 ? 'warning' : 'danger'
                          } 
                        />
                      </div>
                      <div className="progress-area-box">
                        <div className="d-flex justify-content-between align-items-center mb-2 mt-3">
                          <h5>Availability</h5>
                          <Badge color={
                            complianceStatus?.soc2?.availability?.status === 'compliant' ? 'success' : 
                            complianceStatus?.soc2?.availability?.status === 'in-progress' ? 'warning' : 'danger'
                          }>
                            {complianceStatus?.soc2?.availability?.status || 'Compliant'}
                          </Badge>
                        </div>
                        <Progress 
                          value={complianceStatus?.soc2?.availability?.score || 100} 
                          color={
                            complianceStatus?.soc2?.availability?.score >= 90 ? 'success' : 
                            complianceStatus?.soc2?.availability?.score >= 70 ? 'warning' : 'danger'
                          } 
                        />
                      </div>
                      <div className="progress-area-box">
                        <div className="d-flex justify-content-between align-items-center mb-2 mt-3">
                          <h5>Confidentiality</h5>
                          <Badge color={
                            complianceStatus?.soc2?.confidentiality?.status === 'compliant' ? 'success' : 
                            complianceStatus?.soc2?.confidentiality?.status === 'in-progress' ? 'warning' : 'danger'
                          }>
                            {complianceStatus?.soc2?.confidentiality?.status || 'Compliant'}
                          </Badge>
                        </div>
                        <Progress 
                          value={complianceStatus?.soc2?.confidentiality?.score || 100} 
                          color={
                            complianceStatus?.soc2?.confidentiality?.score >= 90 ? 'success' : 
                            complianceStatus?.soc2?.confidentiality?.score >= 70 ? 'warning' : 'danger'
                          } 
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className='col-lg-6 col-md-12 col-sm-12'>
                  <div className='chart-area'>
                    <div className='table-area'>
                      <div className='table-title-area d-flex justify-content-between align-items-center mb-2'>
                        <h4>Compliance Actions</h4>
                      </div>
                      <div className="btn-group-export">
                        <button 
                          className="btn btn-info btn-block mb-2"
                          onClick={() => dispatch(generateComplianceReport({ type: 'soc2' }))}
                        >
                          <i className="icon fa fa-file mr-1"></i>Generate SOC 2 Report
                        </button>
                        <button 
                          className="btn btn-info btn-block mb-2"
                          onClick={() => dispatch(generateComplianceReport({ type: 'audit' }))}
                        >
                          <i className="icon fa fa-download mr-1"></i>Export Audit Trail
                        </button>
                        <button 
                          className="btn btn-warning btn-block mb-2"
                          onClick={() => openModal('dataRequest')}
                        >
                          <i className="icon fa fa-user mr-1"></i>Process Data Subject Request
                        </button>
                        <button 
                          className="btn btn-danger btn-block"
                          onClick={() => openModal('dataPurge')}
                        >
                          <i className="icon fa fa-trash mr-1"></i>Initiate Data Purge
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabPane>
        </TabContent>

        {/* Detail View Modal */}
        {selectedLog && (
          <Modal isOpen={!!selectedLog} toggle={() => setSelectedLog(null)} size="lg" className={`main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal`}>
            <ModalHeader toggle={() => setSelectedLog(null)}>
              <h3><i className="fas fa-file-alt mr-2" />
              Audit Log Details</h3>
            </ModalHeader>
            <ModalBody>
              {/* Action Summary Card */}
              <Card className="mb-3 border-0 bg-light mt-4">
                <CardBody>
                  <Row>
                    <Col md={8}>
                      <div className="d-flex align-items-center mb-2">
                        <i className={`fas ${getActionIcon(selectedLog.action)} fa-2x mr-3`}
                           style={{ color: getActionCategory(selectedLog.action).color }} />
                        <div>
                          <h5 className="mb-1">{formatActionName(selectedLog.action)}</h5>
                          <div>
                            <Badge color="light" className="mr-2">
                              <i className={`fas ${getActionCategory(selectedLog.action).icon} mr-1`} />
                              {getActionCategory(selectedLog.action).name}
                            </Badge>
                            <Badge color={getActionImpact(selectedLog.action).color}>
                              {getActionImpact(selectedLog.action).level} Impact
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col md={4} className="text-end">
                      <Badge color={getStatusBadge(selectedLog.success).color} pill className="px-3 py-2">
                        <i className={`fas ${getStatusBadge(selectedLog.success).icon} mr-1`} />
                        {getStatusBadge(selectedLog.success).text}
                      </Badge>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* Main Details */}
              <Row>
                <Col md={6}>
                  <div className="mb-4">
                    <h6 className="text-uppercase text-muted mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                      <i className="fas fa-user-shield mr-2" />
                      Administrator
                    </h6>
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center mr-3" 
                           style={{ width: '40px', height: '40px', fontSize: '14px', fontWeight: 'bold' }}>
                        {selectedLog.adminId ? 
                          `${selectedLog.adminId.firstName?.[0] || ''}${selectedLog.adminId.lastName?.[0] || ''}` : 
                          'SYS'}
                      </div>
                      <div>
                        <div className="fw-bold">
                          {selectedLog.adminId ? 
                            `${selectedLog.adminId.firstName || 'Unknown'} ${selectedLog.adminId.lastName || ''}` : 
                            'System'}
                        </div>
                        <small className="text-muted">
                          {selectedLog.adminId?.email || 'Automated Action'}
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h6 className="text-uppercase text-muted mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                      <i className="fas fa-bullseye mr-2" />
                      Target Resource
                    </h6>
                    <div className="p-2 bg-light rounded">
                      <strong>{formatTarget(selectedLog.resource, selectedLog.resourceId, selectedLog.metadata)}</strong>
                      {selectedLog.resource && (
                        <div className="mt-1">
                          <small className="text-muted">Type: {selectedLog.resource}</small>
                          {selectedLog.resourceId && (
                            <div><small className="text-muted">ID: {selectedLog.resourceId}</small></div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="mb-4">
                    <h6 className="text-uppercase text-muted mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                      <i className="fas fa-calendar-alt mr-2" />
                      Date & Time
                    </h6>
                    <div className="p-2 bg-light rounded">
                      <div className="fw-bold">
                        {selectedLog.createdAt ? 
                          new Date(selectedLog.createdAt).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          }) : 
                          'Date not available'}
                      </div>
                      <div className="text-muted">
                        {selectedLog.createdAt ? 
                          new Date(selectedLog.createdAt).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          }) : 
                          'Time not available'}
                      </div>
                      {selectedLog.createdAt && (
                        <small className="text-muted">({formatRelativeTime(selectedLog.createdAt)})</small>
                      )}
                    </div>
                  </div>

                  <div className="mb-4">
                    <h6 className="text-uppercase text-muted mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                      <i className="fas fa-network-wired mr-2" />
                      Connection Details
                    </h6>
                    <div className="p-2 bg-light rounded">
                      <div><small><strong>IP Address:</strong> {selectedLog.ipAddress || '127.0.0.1'}</small></div>
                      <div><small><strong>User Agent:</strong> {selectedLog.userAgent || 'Web Browser'}</small></div>
                      <div><small><strong>Session ID:</strong> <code>{selectedLog.sessionId || 'N/A'}</code></small></div>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Error Message if exists */}
              {selectedLog.errorMessage && (
                <Alert color="danger" className="mb-3">
                  <h6 className="alert-heading">
                    <i className="fas fa-exclamation-triangle mr-2" />
                    Error Details
                  </h6>
                  <p className="mb-0">{selectedLog.errorMessage}</p>
                </Alert>
              )}

              {/* Changes Details */}
              {(formatChangeDetails(selectedLog.changes, selectedLog.requestBody, selectedLog.metadata).length > 0) && (
                <>
                  <hr />
                  <h6 className="text-uppercase text-muted mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                    <i className="fas fa-list-alt mr-2" />
                    Action Details
                  </h6>
                  <Card className="border-0 bg-light">
                    <CardBody>
                      <Table size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: '40%' }}>Field</th>
                            <th style={{ width: '60%' }}>Value / Change</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formatChangeDetails(selectedLog.changes, selectedLog.requestBody, selectedLog.metadata)
                            .map((detail, idx) => (
                              <tr key={idx}>
                                <td className="fw-bold text-muted">{detail.field}</td>
                                <td>
                                  {detail.type === 'change' ? (
                                    <div>
                                      <Badge color="light" className="mr-2">
                                        <del>{detail.from || 'empty'}</del>
                                      </Badge>
                                      <i className="fas fa-arrow-right text-muted mx-1" />
                                      <Badge color="success">
                                        {detail.to || 'empty'}
                                      </Badge>
                                    </div>
                                  ) : (
                                    <span className="text-dark">{detail.value || 'N/A'}</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>
                </>
              )}

              {/* Request Body - Only show if complex data */}
              {selectedLog.requestBody && Object.keys(selectedLog.requestBody).length > 5 && (
                <>
                  <hr />
                  <h6 className="text-uppercase text-muted mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                    <i className="fas fa-code mr-2" />
                    Raw Request Data
                  </h6>
                  <pre className="bg-light p-3 rounded" style={{ maxHeight: '200px', overflow: 'auto', fontSize: '0.85rem' }}>
                    <code>{JSON.stringify(selectedLog.requestBody, null, 2)}</code>
                  </pre>
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <button className="btn-style-border" color="secondary" onClick={() => setSelectedLog(null)}>
                Close
              </button>
            </ModalFooter>
          </Modal>
        )}

        {/* Other Modals */}
        <Modal isOpen={showModal && modalType !== 'logDetails'} toggle={closeModal} size="md" className={`main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal`}>
          <ModalHeader toggle={closeModal}>
            <h3>{modalType === 'createApiKey' && 'Generate New API Key'}
            {modalType === 'addIp' && 'Add IP to Whitelist'}
            {modalType === 'dataRequest' && 'Create Data Subject Request'}
            {modalType === 'dataPurge' && 'Initiate Data Purge'}</h3>
          </ModalHeader>
          <ModalBody>
            <Form className="dashboard-form mt-4">

              {modalType === 'createApiKey' && (
                <Form>
                  <FormGroup>
                    <Label>API Key Name</Label>
                    <Input type="text" value={newApiKey['name']} onChange={(e) => setNewApiKey({ ...newApiKey, name: e.target.value })} placeholder="e.g., Production API Key" />
                  </FormGroup>
                  <FormGroup>
                    <Label>Permissions</Label>
                    {['read', 'write', 'delete', 'admin'].map(perm => (
                      <div key={perm} className="custom-control custom-checkbox">
                        <input type="checkbox" className="custom-control-input" id={perm} checked={newApiKey['permissions'].includes(perm)} onChange={(e) => {
                          if (e.target.checked) setNewApiKey({ ...newApiKey, permissions: [...newApiKey['permissions'], perm] });
                          else setNewApiKey({ ...newApiKey, permissions: newApiKey['permissions'].filter(p => p !== perm) });
                        }} />
                        <label className="custom-control-label" htmlFor={perm}>{perm.charAt(0).toUpperCase() + perm.slice(1)}</label>
                      </div>
                    ))}
                  </FormGroup>
                </Form>
              )}
            </Form>
              {modalType === 'addIp' && (
                <Form className="dashboard-form ">
                  <FormGroup>
                    <Label>IP Address</Label>
                    <Input type="text" value={newIp} onChange={(e) => setNewIp(e.target.value)} placeholder="e.g., 192.168.1.1" />
                    <small className="text-muted">Enter a valid IPv4 or IPv6 address</small>
                  </FormGroup>
                </Form>
              )}
              
              {modalType === 'dataRequest' && (
                <Form className="dashboard-form">
                  <FormGroup>
                    <Label>Request Type</Label>
                    <Input 
                      type="select" 
                      value={dataRequestForm.type || ''} 
                      onChange={(e) => setDataRequestForm({...dataRequestForm, type: e.target.value})}
                    >
                      <option value="">Select Type</option>
                      <option value="access">Data Access Request</option>
                      <option value="deletion">Data Deletion Request</option>
                      <option value="portability">Data Portability Request</option>
                      <option value="rectification">Data Rectification Request</option>
                    </Input>
                  </FormGroup>
                  <FormGroup>
                    <Label>Requested By</Label>
                    <Input 
                      type="email" 
                      value={dataRequestForm.requestedBy || ''} 
                      onChange={(e) => setDataRequestForm({...dataRequestForm, requestedBy: e.target.value})}
                      placeholder="user@example.com" 
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Notes</Label>
                    <Input 
                      type="textarea" 
                      value={dataRequestForm.notes || ''} 
                      onChange={(e) => setDataRequestForm({...dataRequestForm, notes: e.target.value})}
                      placeholder="Additional details about the request..." 
                      rows="3"
                    />
                  </FormGroup>
                </Form>
              )}
              
              {modalType === 'dataPurge' && (
                <Form className="dashboard-form">
                  <Alert color="danger">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    <strong>Warning:</strong> This action is irreversible and will permanently delete data according to your retention policies.
                  </Alert>
                  <FormGroup>
                    <Label>Data Type to Purge</Label>
                    <Input 
                      type="select" 
                      value={purgeDataType || ''} 
                      onChange={(e) => setPurgeDataType(e.target.value)}
                    >
                      <option value="">Select Data Type</option>
                      {complianceStatus?.dataRetention?.map((policy, idx) => (
                        <option key={idx} value={policy.dataType}>
                          {policy.dataType} (older than {policy.retentionDays} days)
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                  <FormGroup check>
                    <Label check>
                      <Input 
                        type="checkbox" 
                        checked={confirmPurge} 
                        onChange={(e) => setConfirmPurge(e.target.checked)}
                      />
                      I understand this action cannot be undone
                    </Label>
                  </FormGroup>
                </Form>
              )}
          </ModalBody>
          <ModalFooter>
            {modalType === 'createApiKey' && (
              <button className='btn-style-new' onClick={generateApiKey} disabled={!newApiKey['name'] || newApiKey['permissions'].length === 0}>
                Generate API Key
              </button>
            )}
            {modalType === 'addIp' && (
              <button className='btn-style-new' onClick={addIpToWhitelist} disabled={!newIp}>
                Add IP Address
              </button>
            )}
            {modalType === 'dataRequest' && (
              <button 
                className='btn-style-new' 
                onClick={() => {
                  dispatch(createDataRequest(dataRequestForm));
                  closeModal();
                  setDataRequestForm({ type: '', requestedBy: '', notes: '' });
                }} 
                disabled={!dataRequestForm.type || !dataRequestForm.requestedBy}
              >
                Submit Request
              </button>
            )}
            {modalType === 'dataPurge' && (
              <button 
                className='btn btn-danger' 
                onClick={() => {
                  // In production, this would trigger the actual data purge
                  closeModal();
                  setPurgeDataType('');
                  setConfirmPurge(false);
                }} 
                disabled={!purgeDataType || !confirmPurge}
              >
                Confirm Purge
              </button>
            )}
            <button className='btn-style-border' onClick={closeModal}>
              {modalType === 'logDetails' ? 'Close' : 'Cancel'}
            </button>
          </ModalFooter>
        </Modal>
      </div>
    </>
  );
};

export default SecurityCompliance;